import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Repeat2, ShieldCheck, ChevronUp, ChevronDown, X } from 'lucide-react';

export default function StoriesPage() {
  const [stories, setStories] = useState([]);
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    fetch('/data/impact_stories.json').then(r => r.json()).then(setStories).catch(() => {});
  }, []);

  const goNext = () => { if (current < stories.length - 1) { setDir(1); setCurrent(c => c + 1); } };
  const goPrev = () => { if (current > 0) { setDir(-1); setCurrent(c => c - 1); } };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const story = stories[current];
  if (!story && stories.length === 0) return <div className="flex items-center justify-center h-[80vh] text-slate-400 text-sm">Loading stories...</div>;
  if (!story) return null;

  const metrics = story.metrics || {};

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-56px)] bg-slate-900/5 py-4 px-4" data-testid="stories-page" ref={containerRef}>
      <div className="relative w-full max-w-[400px]">
        {/* Progress bar */}
        <div className="flex gap-1 mb-3">
          {stories.map((s, i) => (
            <div key={s.id || `progress-${i}`} className="flex-1 h-0.5 rounded-full bg-slate-300 overflow-hidden">
              <div className={`h-full rounded-full bg-[#7C3AED] transition-all duration-300 ${i < current ? 'w-full' : i === current ? 'w-1/2' : 'w-0'}`} />
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={current}
            custom={dir}
            initial={{ opacity: 0, y: dir > 0 ? 100 : -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dir > 0 ? -100 : 100 }}
            transition={{ duration: 0.35 }}
            className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] max-h-[75vh]"
            data-testid={`story-${current}`}
          >
            <img src={story.backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30" />

            {/* Top: NGO info */}
            <div className="absolute top-4 left-4 right-12 flex items-center gap-2">
              <img src={story.ngoAvatar} alt="" className="w-8 h-8 rounded-full border-2 border-white" />
              <div>
                <p className="text-white text-sm font-semibold flex items-center gap-1">{story.ngoName} <ShieldCheck className="w-3 h-3 text-emerald-400" /></p>
              </div>
            </div>

            {/* Close */}
            <Link to="/" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center"><X className="w-4 h-4 text-white" /></Link>

            {/* Right side actions */}
            <div className="absolute right-4 bottom-40 flex flex-col gap-4">
              {[{ icon: Heart, label: 'Fund', count: Object.values(metrics)[0] || 0 }, { icon: MessageCircle, label: 'Discuss', count: '' }, { icon: Repeat2, label: 'Share', count: '' }, { icon: ShieldCheck, label: 'Verify', count: '' }].map(a => (
                <button key={a.label} className="flex flex-col items-center gap-1 text-white" data-testid={`story-action-${a.label.toLowerCase()}`}>
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"><a.icon className="w-5 h-5" /></div>
                  <span className="text-[10px]">{a.count ? a.count.toLocaleString() : a.label}</span>
                </button>
              ))}
            </div>

            {/* Bottom content */}
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <h2 className="text-white text-lg font-bold mb-2">{story.title}</h2>
              <p className="text-white/80 text-sm mb-3">{story.overlayText}</p>
              <div className="flex items-center gap-3 flex-wrap mb-4">
                {Object.entries(metrics).slice(0, 3).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-400" />
                    <span className="text-xs text-white font-medium">{typeof v === 'number' ? v.toLocaleString() : v} {k}</span>
                  </div>
                ))}
              </div>
              <Link to={`/project/${story.projectSlug}`} className="inline-flex items-center px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white text-sm font-semibold hover:shadow-lg transition-all" data-testid="story-cta">
                {story.ctaType === 'donate' ? 'Donate Now' : story.ctaType === 'volunteer' ? 'Volunteer' : 'Learn More'}
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-3">
          <button onClick={goPrev} disabled={current === 0} className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center disabled:opacity-30" data-testid="story-prev"><ChevronUp className="w-5 h-5 text-slate-600" /></button>
          <span className="text-sm text-slate-500">{current + 1} / {stories.length}</span>
          <button onClick={goNext} disabled={current === stories.length - 1} className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center disabled:opacity-30" data-testid="story-next"><ChevronDown className="w-5 h-5 text-slate-600" /></button>
        </div>
      </div>
    </div>
  );
}
